/**
 * Connecting the agent's own mailbox (Gmail), through the gateway to notification-service.
 * Errors are left to the caller: read `err.response.status` (404 means the feature is off here) and `err.response.data.code`.
 */
import { apiClient, API_ENDPOINTS } from '../config/api';
import { getFirebaseToken } from '../utils/getFirebaseToken';

const authHeaders = async () => ({ Authorization: `Bearer ${await getFirebaseToken()}` });

export const mailboxService = {
  /** { connected, needsReconnect, provider, address } */
  getStatus: async () => {
    const response = await apiClient.get(API_ENDPOINTS.mailboxStatus, { headers: await authHeaders() });
    return response.data;
  },

  /** The Google page the user is sent to. */
  getConnectUrl: async (provider = 'google') => {
    const response = await apiClient.get(API_ENDPOINTS.mailboxConnect, {
      headers: await authHeaders(),
      params: { provider },
    });
    return response.data.authUrl;
  },

  /** Completes the connection with the code and state Google redirected back with. */
  complete: async (state, code) => {
    const response = await apiClient.post(API_ENDPOINTS.mailboxCallback, { state, code }, { headers: await authHeaders() });
    return response.data;
  },

  disconnect: async () => {
    const response = await apiClient.delete(API_ENDPOINTS.mailboxConnect, { headers: await authHeaders() });
    return response.data;
  },
};

/** A plain sentence for a failed connection step, using the service's own reason where it gave one. */
export const mailboxErrorMessage = (err) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if (status === 400 && message) return message;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 503) return 'Connecting a mailbox is not available right now. Please try again later.';
  return 'Something went wrong. Please try again.';
};
